import { VehicleSavingsCalculatorV2 } from '@/catalog/types';
import { withPageContainerStyles } from '@/common/components/page-layout';
import Spacer from '@/common/components/spacer';
import PDPSectionContainer, { PDPSectionContainerProps } from './pdp-section-container';
import { Button } from '@/shadcn-ui/button';
import { getDynamicCurrency } from '@/util-conversion/currency';
import GradientText from '@/common/components/gradient-text';
import { pluralize } from '@/common/utils/pluralize';
import CostComparisonCard from './cost-comparison-card';

interface CostComparisonProps
  extends Pick<PDPSectionContainerProps, 'onInViewChange' | 'addSectionRef'> {
  savings: VehicleSavingsCalculatorV2;
  onBreakdownClickDelegate: () => void;
}

export default function CostComparison({
  savings,
  onInViewChange,
  addSectionRef,
  onBreakdownClickDelegate,
}: CostComparisonProps) {
  return (
    <PDPSectionContainer
      sectionKey="savings"
      onInViewChange={onInViewChange}
      addSectionRef={addSectionRef}
    >
      <div
        className={withPageContainerStyles(
          'flex max-w-[1440px] flex-col items-center gap-[40px] px-[16px] pb-[88px] md:pb-[120px]'
        )}
      >
        {/* Header */}
        <div className="flex w-full flex-col items-center">
          <h2 className="header-sans-tight hidden text-flexcar-neutral1 md:block">
            Cost comparison
          </h2>
          <h3 className="header-sans-tight block text-flexcar-neutral1 md:hidden">
            Cost comparison
          </h3>
          <Spacer variant="height8" />
          <p className="body-tight text-center text-[18px] text-flexcar-neutral0">
            See how much you&apos;ll save with Flexcar vs. buying the exact same car.
          </p>
        </div>

        {/* Cards */}
        <div className="flex w-full justify-center gap-[16px] md:gap-[24px]">
          <CostComparisonCard
            variant="buying"
            upfrontPayment={savings.upfront_buying_cost}
            carPaymentMonthlyCost={savings.monthly_car_payment}
            insuranceMonthlyCost={savings.monthly_insurance}
          />
          <CostComparisonCard
            variant="flexcar"
            upfrontPayment={savings.upfront_flexcar_cost}
            carPaymentMonthlyCost={savings.flexcar_year_one_and_two_pre_tax}
            insuranceMonthlyCost={savings.flexcar_monthly_insurance_pre_tax}
          />
        </div>

        {/* Total savings */}
        {savings.flexcar_savings_over_ownership_year_term > 0 ? (
          <div className="flex w-full flex-col items-center gap-[8px]">
            {/* Desktop */}
            <GradientText
              className="header-sans-tight hidden tracking-normal md:block"
              gradient="linear-gradient(270deg, #00FFCD -54.58%, #2A00A5 100%)"
              variant="h1"
            >
              {getDynamicCurrency(savings.flexcar_savings_over_ownership_year_term)}
            </GradientText>
            <h3 className="header-sans-tight hidden tracking-normal md:block">
              Total savings over {savings.ownership_years}{' '}
              {pluralize(savings.ownership_years, 'year')}
            </h3>

            {/* Mobile */}
            <GradientText
              className="header-sans-tight block tracking-normal md:hidden"
              gradient="linear-gradient(270deg, #00FFCD -54.58%, #2A00A5 100%)"
              variant="h2"
            >
              {getDynamicCurrency(savings.flexcar_savings_over_ownership_year_term)}
            </GradientText>
            <h4 className="header-sans-tight block tracking-normal md:hidden">
              Total savings over {savings.ownership_years}{' '}
              {pluralize(savings.ownership_years, 'year')}
            </h4>
          </div>
        ) : null}

        {/* Open FSC Modal */}
        <Button
          variant="homepageGradient"
          className="font-[500] text-flexcar-neutral100"
          onClick={onBreakdownClickDelegate}
        >
          See full breakdown
        </Button>
      </div>
    </PDPSectionContainer>
  );
}
